/**
 * Locale Utilities
 * 
 * Provides locale-related utilities without depending on LocaleContext.
 * Uses restaurant settings from localStorage to determine locale.
 */

// Phone patterns by locale code
export const PHONE_PATTERNS = {
  ES: { prefix: "+34", pattern: /^\+?34?\d{9}$/, placeholder: "+34 XXX XXX XXX" },
  PT: { prefix: "+351", pattern: /^\+?351?\d{9}$/, placeholder: "+351 XXX XXX XXX" },
  BR: { prefix: "+55", pattern: /^\+?55?\d{10,11}$/, placeholder: "+55 XX XXXXX XXXX" },
  en: { prefix: "+1", pattern: /^\+?1?\d{10}$/, placeholder: "+1 XXX XXX XXXX" },
};

// Labels by locale - includes all dashboard labels
const LABELS = {
  ES: {
    phone: "Teléfono",
    name: "Nombre",
    address: "Dirección",
    delivery: "Delivery",
    pickup: "Recoger en local",
    dineIn: "Servir en mesa",
    counter: "Balcão",
    subtotal: "Subtotal",
    shipping: "Flete",
    total: "Total",
    revenue: "Facturación confirmada",
    revenueTooltip: "Pedidos pagados o entregados (excluye pendientes y sin pago confirmado)",
    grossRevenue: "Total bruto",
    grossRevenueTooltip: "Todos los pedidos no cancelados, incluidos pendientes o no pagados",
    avgTicket: "Ticket medio",
    deliveryOpen: "DELIVERY ABIERTO",
    deliveryClosed: "DELIVERY CERRADO",
    dailyTracking: "Acompañamiento del día",
    ordersCount: "Pedidos",
    cancelledOrders: "Cancelados",
    reviews: "Reseñas",
    comments: "Comentarios",
    trialDays: "días restantes en tu prueba gratuita",
    viewPlans: "Ver Planes",
    configure: "CONFIGURAR",
    ordersPerDay: "Pedidos por día",
    revenuePerDay: "Ingresos por día",
  },
  PT: {
    phone: "Telefone",
    name: "Nome",
    address: "Endereço",
    delivery: "Entrega",
    pickup: "Retirada no local",
    dineIn: "Servir na mesa",
    counter: "Balcão",
    subtotal: "Subtotal",
    shipping: "Frete",
    total: "Total",
    revenue: "Faturamento confirmado",
    revenueTooltip: "Pedidos pagos ou entregues (exclui pendentes e sem pagamento confirmado)",
    grossRevenue: "Total bruto",
    grossRevenueTooltip: "Todos os pedidos não cancelados, incluindo pendentes ou não pagos",
    avgTicket: "Ticket médio",
    deliveryOpen: "DELIVERY ABERTO",
    deliveryClosed: "DELIVERY FECHADO",
    dailyTracking: "Acompanhamento do dia",
    ordersCount: "Pedidos",
    cancelledOrders: "Cancelados",
    reviews: "Avaliações",
    comments: "Comentários",
    trialDays: "dias restantes no seu período de teste",
    viewPlans: "Ver Planos",
    configure: "CONFIGURAR",
    ordersPerDay: "Pedidos por dia",
    revenuePerDay: "Receita por dia",
  },
  BR: {
    phone: "Telefone",
    name: "Nome",
    address: "Endereço",
    delivery: "Entrega",
    pickup: "Retirada no local",
    dineIn: "Servir na mesa",
    counter: "Balcão",
    subtotal: "Subtotal",
    shipping: "Frete",
    total: "Total",
    revenue: "Faturamento confirmado",
    revenueTooltip: "Pedidos pagos ou entregues (exclui pendentes e sem pagamento confirmado)",
    grossRevenue: "Total bruto",
    grossRevenueTooltip: "Todos os pedidos não cancelados, incluindo pendentes ou não pagos",
    avgTicket: "Ticket médio",
    deliveryOpen: "DELIVERY ABERTO",
    deliveryClosed: "DELIVERY FECHADO",
    dailyTracking: "Acompanhamento do dia",
    ordersCount: "Pedidos",
    cancelledOrders: "Cancelados",
    reviews: "Avaliações",
    comments: "Comentários",
    trialDays: "dias restantes no seu período de teste",
    viewPlans: "Ver Planos",
    configure: "CONFIGURAR",
    ordersPerDay: "Pedidos por dia",
    revenuePerDay: "Receita por dia",
  },
  en: {
    phone: "Phone",
    name: "Name",
    address: "Address",
    delivery: "Delivery",
    pickup: "Pickup",
    dineIn: "Dine In",
    subtotal: "Subtotal",
    shipping: "Shipping",
    total: "Total",
    revenue: "Confirmed Revenue",
    revenueTooltip: "Paid or delivered orders (excludes pending and unconfirmed payments)",
    grossRevenue: "Gross Total",
    grossRevenueTooltip: "All non-cancelled orders, including pending or unpaid",
    avgTicket: "Avg Ticket",
    deliveryOpen: "DELIVERY OPEN",
    deliveryClosed: "DELIVERY CLOSED",
    dailyTracking: "Daily Tracking",
    ordersCount: "Orders",
    cancelledOrders: "Cancelled",
    reviews: "Reviews",
    comments: "Comments",
    trialDays: "days remaining in your free trial",
    viewPlans: "View Plans",
    configure: "CONFIGURE",
    ordersPerDay: "Orders per day",
    revenuePerDay: "Revenue per day",
  },
};

// Phone prefixes by locale
const PHONE_PREFIXES = {
  ES: "+34",
  PT: "+351",
  BR: "+55",
  en: "+1",
};

/**
 * Get the current locale code from restaurant settings
 * @returns {string} Locale code (ES, PT, BR, en)
 */
export function getLocaleCode() {
  try {
    const restaurantData = localStorage.getItem('kaiso_selected_restaurant');
    if (restaurantData) {
      const restaurant = JSON.parse(restaurantData);
      // Map country to locale code
      if (restaurant.country === 'BR') return 'BR';
      if (restaurant.country === 'PT') return 'PT';
      if (restaurant.country === 'ES' || restaurant.country === 'España') return 'ES';
      if (restaurant.locale) {
        if (restaurant.locale.includes('BR')) return 'BR';
        if (restaurant.locale.includes('PT')) return 'PT';
        if (restaurant.locale.includes('ES')) return 'ES';
      }
    }
  } catch (e) {
    // Ignore errors
  }
  return 'ES'; // Default to Spanish
}

/**
 * Get labels for the current locale
 * @param {string} [localeCode] - Optional locale code override
 * @returns {Object} Labels object
 */
export function getLabels(localeCode) {
  const code = localeCode || getLocaleCode();
  return LABELS[code] || LABELS.ES;
}

/**
 * Translate a label key
 * @param {string} key - Label key to translate
 * @param {string} [localeCode] - Optional locale code override
 * @returns {string} Translated label or the key if not found
 */
export function t(key, localeCode) {
  const labels = getLabels(localeCode);
  return labels[key] || key;
}

/**
 * Get phone prefix for the current locale
 * @param {string} [localeCode] - Optional locale code override
 * @returns {string} Phone prefix
 */
export function getPhonePrefix(localeCode) {
  const code = localeCode || getLocaleCode();
  return PHONE_PREFIXES[code] || PHONE_PREFIXES.ES;
}

/**
 * Get phone pattern for validation
 * @param {string} [localeCode] - Optional locale code override
 * @returns {Object} Phone pattern object with prefix, pattern, placeholder
 */
export function getPhonePattern(localeCode) {
  const code = localeCode || getLocaleCode();
  return PHONE_PATTERNS[code] || PHONE_PATTERNS.ES;
}

/**
 * Get locale object with all settings (compatibility layer)
 * @param {string} [localeCode] - Optional locale code override
 * @returns {Object} Locale object with labels, phonePrefix, etc.
 */
export function getLocaleSettings(localeCode) {
  const code = localeCode || getLocaleCode();
  const labels = getLabels(code);
  const phonePrefix = getPhonePrefix(code);
  
  return {
    code,
    labels,
    phonePrefix,
    currency: code === 'BR' ? 'BRL' : 'EUR',
    currencySymbol: code === 'BR' ? 'R$' : '€',
  };
}

/**
 * Map locale code to language for external services (tour, etc.)
 * @param {string} [localeCode] - Optional locale code override
 * @returns {string} Language code (pt, es, en)
 */
export function getLanguageFromLocale(localeCode) {
  const code = localeCode || getLocaleCode();
  switch (code) {
    case 'BR':
    case 'PT':
      return 'pt';
    case 'ES':
      return 'es';
    default:
      return 'en';
  }
}
